import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  Eye,
  FileDown,
  Image,
  Link as LinkIcon,
  QrCode,
  Search,
  User,
} from "lucide-react";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toYmd(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function startOfMonthYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
}

function formatDateTime(str) {
  if (!str) return "";
  return str.replace("T", " ").substring(0, 16);
}

function groupKeyFromDataProduzione(data_produzione, mode) {
  if (!data_produzione) return "Senza data";
  const s = data_produzione.replace("T", " ");
  const ymd = s.substring(0, 10);
  if (mode === "day") return ymd;
  if (mode === "month") return ymd.substring(0, 7);
  return "";
}

function prettyGroupLabel(key, mode) {
  if (!key) return "";
  if (mode === "day") return `Giorno ${key}`;
  if (mode === "month") return `Mese ${key}`;
  return key;
}

export default function StoricoEtichette() {
  const [etichette, setEtichette] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [groupBy, setGroupBy] = useState("none");

  const [error, setError] = useState("");

  const buildQuery = () => {
    const params = new URLSearchParams();
    params.set("limit", "200");

    const s = search.trim();
    if (s) params.set("search", s);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);

    return params.toString();
  };

  const loadData = () => {
    setLoading(true);
    setError("");

    fetch(`/api/etichette_list.php?${buildQuery()}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setEtichette(data);
        else setError(data?.error || "Errore nel caricamento delle etichette");
      })
      .catch(() => setError("Errore di rete"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getVisualizzaDdtUrl = (ddt_ids) => {
    if (!ddt_ids) return null;
    const cleaned = String(ddt_ids)
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .join(",");
    if (!cleaned) return null;
    return `/api/visualizza_ddt.php?ids=${encodeURIComponent(cleaned)}`;
  };

  const ddtCount = (ddt_ids) => {
    if (!ddt_ids) return 0;
    return String(ddt_ids)
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean).length;
  };

  const getOperatoreLabel = (e) => {
    return e?.operatore_nome_reale || e?.operatore || "—";
  };

  const buildPdfUrl = (id) => {
    if (!id) return null;
    return `/api/etichette_pdf.php?id=${id}&format=80x50&download=1&_=${Date.now()}`;
  };

  const buildImageUrl = (id) => {
    if (!id) return null;
    return `/api/etichette_image.php?id=${id}&format=80x50&download=1&_=${Date.now()}`;
  };

  const openPdf = (id) => {
    const url = buildPdfUrl(id);
    if (!url) return;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = url;
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openImage = (id) => {
    const url = buildImageUrl(id);
    if (!url) return;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = url;
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const grouped = useMemo(() => {
    if (groupBy === "none") return [{ key: "", items: etichette }];

    const map = new Map();
    for (const e of etichette) {
      const key = groupKeyFromDataProduzione(e.data_produzione, groupBy);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    const keys = Array.from(map.keys()).sort((a, b) => (a < b ? 1 : -1));
    return keys.map((k) => ({ key: k, items: map.get(k) }));
  }, [etichette, groupBy]);

  const setPresetOggi = () => {
    const today = toYmd(new Date());
    setDateFrom(today);
    setDateTo(today);
    setTimeout(loadData, 0);
  };

  const setPresetUltimi7 = () => {
    const d = new Date();
    const to = toYmd(d);
    d.setDate(d.getDate() - 6);
    const from = toYmd(d);
    setDateFrom(from);
    setDateTo(to);
    setTimeout(loadData, 0);
  };

  const setPresetQuestoMese = () => {
    setDateFrom(startOfMonthYmd());
    setDateTo(toYmd(new Date()));
    setTimeout(loadData, 0);
  };

  const clearDates = () => {
    setDateFrom("");
    setDateTo("");
    setTimeout(loadData, 0);
  };

  return (
    <div>
      <div className="bg-white p-4 rounded-[24px] shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadData();
          }}
          className="relative w-full md:w-96"
        >
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca lotto o preparazione..."
            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-sm focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all"
          />
        </form>

        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          <button
            type="button"
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              loadData();
            }}
            className="px-4 py-2 rounded-full bg-[#003B71] text-white text-sm font-bold shadow-md whitespace-nowrap"
          >
            Tutti
          </button>

          <button
            type="button"
            onClick={setPresetOggi}
            className="px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 whitespace-nowrap"
          >
            Oggi
          </button>

          <button
            type="button"
            onClick={setPresetUltimi7}
            className="px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 whitespace-nowrap"
          >
            7 Giorni
          </button>

          <button
            type="button"
            onClick={setPresetQuestoMese}
            className="px-4 py-2 rounded-full bg-white border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 whitespace-nowrap"
          >
            Questo mese
          </button>

          <div className="h-6 w-px bg-gray-200 mx-1" />

          <div className="flex items-center gap-2 whitespace-nowrap">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 rounded-[14px] bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-[#003B71]"
              title="Da"
            />
            <span className="text-gray-400 text-sm">→</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 rounded-[14px] bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-[#003B71]"
              title="A"
            />

            <button
              type="button"
              onClick={loadData}
              className="px-4 py-2 rounded-[14px] bg-[#EEF2FF] text-[#003B71] text-sm font-bold hover:bg-[#003B71] hover:text-white transition-all"
              title="Applica date"
            >
              Applica
            </button>

            <button
              type="button"
              onClick={clearDates}
              className="px-4 py-2 rounded-[14px] bg-white border border-gray-200 text-gray-600 text-sm font-bold hover:border-gray-400 transition-all"
              title="Reset date"
            >
              Reset
            </button>

            <div className="h-6 w-px bg-gray-200 mx-1" />

            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-3 py-2 rounded-[14px] bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-[#003B71]"
              title="Raggruppa"
            >
              <option value="none">Nessun gruppo</option>
              <option value="day">Per giorno</option>
              <option value="month">Per mese</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-[18px] border border-red-100 bg-red-50/60 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Caricamento etichette...</div>
      ) : etichette.length === 0 ? (
        <div className="text-sm text-gray-500">
          Nessuna etichetta trovata. Generane una dalla sezione “Gestione”.
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map((g) => (
            <div key={g.key || "all"} className="space-y-4">
              {groupBy !== "none" && (
                <div className="flex items-center justify-between px-1">
                  <div className="text-sm font-bold text-gray-700">
                    {prettyGroupLabel(g.key, groupBy)}
                  </div>
                  <div className="text-xs text-gray-400">
                    {g.items.length} etichette
                  </div>
                </div>
              )}

              {g.items.map((e) => {
                const visualizzaUrl = getVisualizzaDdtUrl(e.ddt_ids);
                const count = ddtCount(e.ddt_ids);
                const statusDot = count > 0 ? "bg-green-500" : "bg-gray-300";

                return (
                  <div
                    key={e.id}
                    className="bg-white rounded-[20px] p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all flex flex-col md:flex-row gap-4 md:items-center justify-between group"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-[10px] font-mono bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md">
                          Lotto: {e.lotto || `#${e.id}`}
                        </span>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`} />
                      </div>

                      <h3 className="text-xl font-bold text-[#003B71] truncate mb-2">
                        {e.preparazione || "—"}
                      </h3>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDateTime(e.data_produzione)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4 text-gray-400" />
                          {getOperatoreLabel(e)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-6">
                      <div className="flex items-center gap-2 bg-[#F6F8FA] px-3 py-2 rounded-xl text-xs font-medium text-gray-600 cursor-pointer hover:bg-blue-50 hover:text-[#003B71] transition-colors">
                        <LinkIcon className="w-3 h-3" />
                        {count} DDT collegati
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openPdf(e.id)}
                          className="w-10 h-10 rounded-[12px] bg-[#E0F7F2] text-[#008F70] flex items-center justify-center hover:bg-[#008F70] hover:text-white transition-all shadow-sm"
                          title="Scarica PDF"
                        >
                          <FileDown className="w-5 h-5" />
                        </button>

                        <button
                          type="button"
                          onClick={() => openImage(e.id)}
                          className="w-10 h-10 rounded-[12px] bg-[#EEF2FF] text-[#003B71] flex items-center justify-center hover:bg-[#003B71] hover:text-white transition-all shadow-sm"
                          title="Scarica PNG"
                        >
                          <Image className="w-5 h-5" />
                        </button>

                        {visualizzaUrl ? (
                          <a
                            href={visualizzaUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="w-10 h-10 rounded-[12px] bg-[#EEF2FF] text-[#003B71] flex items-center justify-center hover:bg-[#003B71] hover:text-white transition-all shadow-sm"
                            title="Vedi DDT collegati"
                          >
                            <Eye className="w-5 h-5" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="w-10 h-10 rounded-[12px] bg-[#EEF2FF] text-[#003B71]/40 flex items-center justify-center cursor-not-allowed"
                            title="Nessun DDT collegato"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        )}

                        {e.qr_path ? (
                          <a
                            href={`/${e.qr_path}`}
                            target="_blank"
                            rel="noreferrer"
                            className="w-10 h-10 rounded-[12px] bg-white border border-gray-200 text-gray-400 flex items-center justify-center hover:text-gray-800 hover:border-gray-400 transition-all"
                            title="Apri QR"
                          >
                            <QrCode className="w-5 h-5" />
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className="w-10 h-10 rounded-[12px] bg-white border border-gray-200 text-gray-300 flex items-center justify-center cursor-not-allowed"
                            title="QR non disponibile"
                          >
                            <QrCode className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
